/**
 * Wire shapes for /api/v2/admin/subscriptions/* — cycle-16 closure shipped a
 * snake_case wire-boundary serializer + reshaped fetchSubscriptionDetail to
 * the legacy {id, name, icon, features, options} projection. The FE can
 * consume the responses verbatim (no normalize() helper needed).
 *
 * Phase E Cycle 7 — status / type / duration_type / limit-type fields
 * now canonical BE enum strings.
 */

// Phase E Cycle 7 canonical (BE billing.constants).
export enum SubscriptionStatus {
  Active = 'active',
  Inactive = 'inactive',
  Archived = 'archived'
}

export enum SubscriptionDurationType {
  Month = 'month',
  Year = 'year',
  Free = 'free'
}

export enum SubscriptionPriceStatus {
  Active = 'active',
  Inactive = 'inactive'
}

export enum SubscriptionLimitType {
  Limited = 'limited',
  Unlimited = 'unlimited'
}

export enum UserSubscriptionStatus {
  Active = 'active',
  Archive = 'archive'
}

export enum SubscriptionPlanType {
  Stripe = 'stripe',
  Paypal = 'paypal',
  ApplePay = 'apple_pay'
}

export interface SubscriptionListRow {
  id: number;
  name: Record<string, string>;
  icon: string | null;
}

export interface SubscriptionListResponse {
  results: SubscriptionListRow[];
  total: number;
}

export interface SubscriptionFeatureRow {
  id: number;
  icon: string | null;
  name: string;
  feature: string | null;
  is_active: boolean;
}

export interface SubscriptionPriceOption {
  price: number;
  most_popular: boolean;
  duration_type: SubscriptionDurationType;
  source: Record<string, string> | null;
}

export interface SubscriptionDetail {
  id: number;
  name: Record<string, string>;
  icon: string | null;
  features: SubscriptionFeatureRow[];
  options: SubscriptionPriceOption[];
}

export interface SubscriptionPriceOptionInput {
  price: number;
  most_popular?: boolean;
  duration_type: SubscriptionDurationType;
  source?: Record<string, string>;
}

export interface SubscriptionInput {
  name: Record<string, string>;
  icon?: string | null;
  is_free?: boolean;
  combine: number[];
  options?: SubscriptionPriceOptionInput[];
}

// ── Feature items ────────────────────────────────────────────────────────

export interface FeatureItemOptionRow {
  id: number;
  feature_item_option_id: number;
  name: Record<string, string>;
  type: SubscriptionLimitType;
  limit: number;
  duration_type: SubscriptionDurationType;
}

export interface FeatureItemRow {
  id: number;
  subscription_feature_id: number;
  unit: number | null;
  value: number | null;
}

export interface FeatureItemDetail extends FeatureItemRow {
  options: FeatureItemOptionRow[];
}

export interface FeatureItemListResponse {
  results: FeatureItemRow[];
  total: number;
}

export interface FeatureItemOptionInput {
  name: Record<string, string>;
  type: SubscriptionLimitType;
  limit: number;
  duration_type: SubscriptionDurationType;
}

export interface FeatureItemInput {
  subscription_feature_id: number;
  unit?: number;
  value?: number;
  options?: FeatureItemOptionInput[];
}

// ── Features ────────────────────────────────────────────────────────────

export interface SubscriptionFeatureListRow {
  id: number;
  icon: string | null;
  name: string;
}
